import { useMemo, useState } from 'react';
import type { ChatBalanceSource, HistoryRange } from '@wave/shared';
import { useSetChatBalanceSource } from '../../api/mutations';
import { Modal } from '../Modal';
import { EChart, type EChartsOption } from '../EChart';
import { useChatHistoryDetail } from './useBalanceHistory';
import { BASE_GRID, CHART_COLORS } from './ChartsPanel';
import { fmtTs } from './TrendChart';

const RANGES: { key: HistoryRange; label: string }[] = [
  { key: '24h', label: '24ч' },
  { key: '7d', label: '7д' },
  { key: '30d', label: '30д' },
  { key: '90d', label: '90д' },
  { key: 'all', label: 'Всё' },
];

const CCY_PALETTE = ['#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777'];

const SOURCE_OPTIONS: { key: ChatBalanceSource; label: string; hint: string }[] = [
  {
    key: 'auto',
    label: 'Авто',
    hint: 'Если чат есть во внешнем API — берём его цифры; иначе разбор сообщений.',
  },
  {
    key: 'api',
    label: 'Внешний API',
    hint: 'Только внешний API. Если он про чат молчит, чат останется пустым.',
  },
  {
    key: 'telegram',
    label: 'Телеграм',
    hint: 'Только разбор сообщений листенером; цифры внешнего API игнорируются.',
  },
];

/**
 * Выбор источника истины по остаткам чата. Показывается только для чатов
 * листенера (у остальных настройка неприменима — `balanceSource === null`)
 * и только с правом chat-balances.manage.
 */
function BalanceSourcePicker({
  chatId,
  value,
}: {
  chatId: string;
  value: ChatBalanceSource;
}) {
  const setSource = useSetChatBalanceSource();
  const active = SOURCE_OPTIONS.find((o) => o.key === value) ?? SOURCE_OPTIONS[0]!;
  return (
    <div className="mb-3 rounded-md border border-border bg-bg2 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-text2">Источник остатков</span>
        <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
          {SOURCE_OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              disabled={setSource.isPending}
              title={o.hint}
              onClick={() => setSource.mutate({ chatId, source: o.key })}
              className={`h-7 rounded px-2.5 text-xs font-medium transition-colors duration-150 disabled:opacity-50 ${value === o.key ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        {setSource.isError && (
          <span className="text-xs text-danger">Не удалось сохранить</span>
        )}
      </div>
      <p className="mt-1.5 text-xs text-text2">{active.hint}</p>
    </div>
  );
}

/** История одного чата: net USDT или линии по валютам. */
export function ChatHistoryModal({
  chatId,
  title,
  balanceSource,
  canManage,
  onClose,
}: {
  chatId: string;
  title: string;
  /** Настройка чата; null — чат не из listening_chats, переключать нечего. */
  balanceSource: ChatBalanceSource | null;
  canManage: boolean;
  onClose: () => void;
}) {
  const [range, setRange] = useState<HistoryRange>('7d');
  const [view, setView] = useState<'net' | 'ccy'>('net');
  const { data, isLoading } = useChatHistoryDetail(chatId, range);

  const option = useMemo<EChartsOption>(() => {
    if (!data) return {};
    if (view === 'net') {
      const pts = data.net;
      return {
        animation: false,
        grid: BASE_GRID,
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
        xAxis: { type: 'category', data: pts.map((p) => p.ts), axisLabel: { color: CHART_COLORS.text, formatter: (v: string) => fmtTs(v, range) } },
        yAxis: { type: 'value', axisLabel: { color: CHART_COLORS.text }, splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
        dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
        series: [
          {
            name: 'Net USDT',
            type: 'line',
            data: pts.map((p) => Number(p.netUsdt)),
            showSymbol: false,
            lineStyle: { width: 2, color: CHART_COLORS.net },
            itemStyle: { color: CHART_COLORS.net },
            areaStyle: { opacity: 0.08 },
          },
        ],
      };
    }
    const allTs = [...new Set(data.ccys.flatMap((c) => c.points.map((p) => p.ts)))].sort();
    return {
      animation: false,
      grid: BASE_GRID,
      tooltip: { trigger: 'axis' },
      legend: { top: 0, textStyle: { color: CHART_COLORS.text } },
      xAxis: { type: 'category', data: allTs, axisLabel: { color: CHART_COLORS.text, formatter: (v: string) => fmtTs(v, range) } },
      yAxis: { type: 'value', axisLabel: { color: CHART_COLORS.text }, splitLine: { lineStyle: { color: CHART_COLORS.grid } } },
      dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 8 }],
      series: data.ccys.map((c, i) => {
        const byTs = new Map(c.points.map((p) => [p.ts, Number(p.amount)]));
        return {
          name: c.ccy,
          type: 'line' as const,
          data: allTs.map((ts) => byTs.get(ts) ?? null),
          connectNulls: true,
          showSymbol: false,
          lineStyle: { width: 1.5, color: CCY_PALETTE[i % CCY_PALETTE.length] },
          itemStyle: { color: CCY_PALETTE[i % CCY_PALETTE.length] },
        };
      }),
    };
  }, [data, view, range]);

  const empty = !isLoading && (data?.net.length ?? 0) < 2;

  return (
    <Modal title={`История · ${title}`} onClose={onClose} size="lg">
      {canManage && balanceSource !== null && (
        <BalanceSourcePicker chatId={chatId} value={balanceSource} />
      )}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
          <button
            onClick={() => setView('net')}
            className={`h-8 rounded px-3 text-sm font-medium transition-colors duration-150 ${view === 'net' ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'}`}
          >
            Net USDT
          </button>
          <button
            onClick={() => setView('ccy')}
            className={`h-8 rounded px-3 text-sm font-medium transition-colors duration-150 ${view === 'ccy' ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'}`}
          >
            По валютам
          </button>
        </div>
        <div className="inline-flex rounded-md border border-border bg-bg p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`h-8 rounded px-2.5 text-xs font-medium transition-colors duration-150 ${range === r.key ? 'bg-accent text-white shadow-xs' : 'text-text2 hover:bg-bg2 hover:text-text'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {isLoading && <div className="h-64 animate-pulse rounded bg-bg3" />}
      {empty && (
        <div className="flex h-64 items-center justify-center text-sm text-text2">
          Для этого чата ещё мало истории.
        </div>
      )}
      {!isLoading && !empty && <EChart option={option} height={380} />}
    </Modal>
  );
}
